import React, { useState, useMemo, useEffect, useRef } from "react";
import { searchOnePiece, OP_CARD_COUNT, baseCode, printLabel, setCodeOf, printsOf } from "./onePieceIndex";
import { opImageUrl, DON_ART } from "./onePieceCards";
import { knownSets, loadSets } from "./onePieceSets";
import { knownDon, loadDon, searchDon } from "./onePieceDon";
import { fetchOnePiecePrices } from "../../shared";
import { formatUSD } from "../../shared/utils";
import { CardZoom, CardLinks } from "../../ui";

// How many matches to render at once. Every match is found — this only limits
// how many card images the browser is asked to load in one go.
const PAGE = 60;
const DON_PAGE = 12;

// Search runs against the bundled catalogue, so it costs no API credits and
// works with no connection. Only the card art needs the network.
export default function OnePieceSearch({
  onAdd,
  isOnList,
  targetName,
  /* On the One Piece list this is a panel you open to add cards. As the app's
     Search screen it is the whole point of the page, so it starts open and has
     no Done button to close itself with. */
  alwaysOpen = false,
  onAddToCollection,
  isInCollection,
  /* Only on the Search screen, not the panel you open from a list. There the
     job is picking cards to add, and sending someone to another screen
     mid-pick loses the list they were filling. */
  onInspect,
}) {
  const [open, setOpen] = useState(alwaysOpen);
  const [query, setQuery] = useState("");
  const [added, setAdded] = useState(0);
  const [zoom, setZoom] = useState(null);

  const [shown, setShown] = useState(PAGE);
  /* A smaller first helping than the numbered cards get. These sit above them,
     and "don" matches all 187 — a full page of DON art before the cards you
     searched for would be its own kind of burying. */
  const [donShown, setDonShown] = useState(DON_PAGE);

  /* Two things the bundled catalogue does not carry: the names of sets, and
     DON!! cards. Both are fetched once and held, and the search works without
     either — it just finds fewer things, which is what it did before. */
  const [sets, setSets] = useState(knownSets);
  const [don, setDon] = useState(knownDon);

  useEffect(() => {
    if (!open) return;
    let live = true;
    /* Only when there is something to say. Both of these answer with an empty
       result rather than throwing when the source is unreachable, and setting
       empty over empty is a re-render that changes nothing on screen. */
    loadSets().then((s) => { if (live && s && Object.keys(s).length) setSets(s); });
    loadDon().then((d) => { if (live && d.length) setDon(d); });
    return () => { live = false; };
  }, [open]);

  /* Kept apart rather than concatenated, which was the first attempt and was
     wrong in the obvious case: "don" matches 62 Donquixote prints, the grid
     shows 60 at a time, and the DON cards the word actually names landed on
     the second page. Two labelled groups say which is which and neither can
     bury the other — and "don" is genuinely ambiguous, so answering it with
     both, named, is more honest than ranking one above the other. */
  const found = useMemo(() => (open ? searchOnePiece(query, sets) : []), [query, open, sets]);

  /* Narrowing what was found, after it was found.
   *
   * The Pokemon search has had set and bulk filters for a long time; this one,
   * with 3,654 cards in it, had nothing. Three things a One Piece collector
   * actually sorts by: which set, which rarity, and whether it is the base
   * print or an alt art — because alt arts are what people hunt, and a search
   * for Luffy is sixty results of which the six that matter are the parallels.
   *
   * Display filters, like the bulk filter: the search is already done and cost
   * nothing, so this is about not scrolling past what you did not mean. */
  const [setFilter, setSetFilter] = useState("");
  const [rarityFilter, setRarityFilter] = useState("");
  const [altOnly, setAltOnly] = useState(false);
  /* DON!! cards are a separate hunt from numbered ones. Somebody pricing a
     binder of Luffys does not want the DON section above every search, and
     somebody hunting gold DONs does not want anything else. Remembered, since
     it is a preference about how you collect and not about one search. */
  const [hideDon, setHideDon] = useState(() => {
    try { return window.localStorage.getItem("ppt_op_hide_don_v1") === "1"; } catch { return false; }
  });
  const toggleDon = (hide) => {
    setHideDon(hide);
    try { window.localStorage.setItem("ppt_op_hide_don_v1", hide ? "1" : "0"); } catch { /* fine */ }
  };

  const isAlt = (id) => /_p\d+$/i.test(id);

  /* A print's rarity is its card's rarity.
   *
   * The catalogue leaves rarity blank on 975 of its 1,147 alt prints, so
   * filtering on it dropped every alt art of a Secret Rare while keeping the
   * base — "Secret Rare" returned one Luffy when the card has four prints,
   * and the three that went missing are the ones worth $80 to $8,490. An alt
   * art of a Secret Rare is a Secret Rare; it inherits from the base print
   * of the same card number. */
  const rarityOf = useMemo(() => {
    const byCard = new Map();
    for (const r of found) {
      if (r.rarity && !isAlt(r.id)) byCard.set(baseCode(r.id), r.rarity);
    }
    return (r) => {
      if (r.rarity) return r.rarity;
      const code = baseCode(r.id);
      if (byCard.has(code)) return byCard.get(code);
      // The base print was not among what was found; ask the catalogue.
      const base = printsOf(code).find((p) => p.rarity);
      const rarity = base?.rarity || "";
      byCard.set(code, rarity);
      return rarity;
    };
  }, [found]);

  const results = useMemo(
    () =>
      found.filter(
        (r) =>
          (!setFilter || setCodeOf(r.id) === setFilter) &&
          (!rarityFilter || rarityOf(r) === rarityFilter) &&
          (!altOnly || isAlt(r.id))
      ),
    [found, setFilter, rarityFilter, altOnly, rarityOf]
  );

  /* The choices offered are the ones present in what was found, so a filter
     never turns a page of results into an empty one for want of a value that
     was not there anyway. */
  const setsFound = useMemo(
    () => [...new Set(found.map((r) => setCodeOf(r.id)))].sort(),
    [found]
  );
  const raritiesFound = useMemo(
    () => [...new Set(found.map(rarityOf).filter(Boolean))].sort(),
    [found, rarityOf]
  );
  const filtering = Boolean(setFilter || rarityFilter || altOnly);

  const donResults = useMemo(() => {
    if (!open || hideDon) return [];
    return searchDon(query, don).map((d) => ({
      id: d.id,
      name: d.name,
      rarity: "DON!!",
      don: true,
      label: d.label,
      set: d.set,
      image: d.image,
      price: d.price,
    }));
  }, [query, open, don, hideDon]);
  const tooShort = query.trim().length === 1;
  const visible = results.slice(0, shown);
  const visibleDon = donResults.slice(0, donShown);

  /* The prints of one card can differ by 300x — OP13-118 runs from $28 to
     $8,490 — so picking by name alone is the most expensive mistake available
     here. The price is shown next to the art so the choice is made on the two
     things that actually tell prints apart. */
  const [prices, setPrices] = useState({});
  const asked = useRef(new Set());

  useEffect(() => {
    // DON cards come down with their price attached, so asking again would be
    // a call that can only return what is already on screen.
    const need = visible.filter((r) => !r.don).map((r) => r.id)
      .filter((id) => !asked.current.has(id));
    if (!need.length) return;
    need.forEach((id) => asked.current.add(id));
    let cancelled = false;
    fetchOnePiecePrices(need)
      .then((p) => { if (!cancelled) setPrices((prev) => ({ ...prev, ...p })); })
      .catch(() => {
        // A price that won't load is a blank, not a broken search — the
        // catalogue and the art are bundled and still work offline.
        need.forEach((id) => asked.current.delete(id));
      });
    return () => { cancelled = true; };
  }, [visible]);

  // Closed by default: you come here to look at your list, not to search.
  if (!open) {
    return (
      <div className="op-search op-search--closed">
        <button className="btn btn-primary" onClick={() => setOpen(true)}>+ Add cards</button>
        <span className="op-search-note">
          {OP_CARD_COUNT.toLocaleString()} cards, searched on your device
          {added > 0 && ` · ${added} added just now`}
        </span>
      </div>
    );
  }

  const toCard = (r) =>
    r.don
      ? {
          id: r.id,
          don: true,                  // so the card's page knows not to look for prints
          name: r.name,
          number: r.id,               // prices key on this, same as any other
          /* There is no number printed on a DON card, so the set is the only
             thing that tells two of them apart on sight. */
          displayNumber: r.set || "DON!!",
          setName: r.set || "DON!!",
          note: r.label,
          imageUrl: r.image || DON_ART,
          marketPrice: r.price ?? null,
          status: "want",
          have: false,
        }
      : {
          id: r.id,                       // our code, e.g. OP13-118_p2
          name: r.name,
          number: r.id,                   // prices key on this, suffix and all
          displayNumber: baseCode(r.id),  // what's actually printed on the card
          setName: baseCode(r.id).split("-")[0],
          note: printLabel(r.id, r.rarity),
          imageUrl: opImageUrl(r.id),
          status: "want",
          have: false,
        };

  // What the tile shows, which differs only because a DON card has no number.
  const artOf = (r) => (r.don ? r.image || DON_ART : opImageUrl(r.id));
  const labelOf = (r) => (r.don ? r.label : printLabel(r.id, r.rarity));
  const codeOf = (r) => (r.don ? r.set || "DON!!" : baseCode(r.id));

  /* One tile, both groups. They differ in where their price comes from and in
     having no other prints to offer, and in nothing else — a second copy of
     this markup would be two places to fix the next thing found in it. */
  const tile = (r) => {
    const already = isOnList?.(r.id);
    const label = labelOf(r);
    const price = r.don ? r.price : prices[r.id];
    return (
      <div key={r.id} className="card">
        <button
          type="button"
          className="card-zoom-btn"
          onClick={() => setZoom({ name: r.name, imageUrl: artOf(r), number: codeOf(r), setName: label })}
          title={`Enlarge ${r.name} — ${label}`}
          aria-label={`Enlarge ${r.name}, ${label}`}
        >
          <img className="card-image" src={artOf(r)} alt={`${r.name}, ${label}`} loading="lazy" />
        </button>
        <div className="card-name">{r.name}</div>
        {/* The printed code, without the suffix we invented — or, for a DON
            card, the set, because nothing is printed on one. */}
        <div className="card-number">{codeOf(r)}</div>
        <div className="wl-note">{label}</div>
        {price != null ? (
          <div className="card-price">{formatUSD(price)}</div>
        ) : (
          <div className="wl-price-none" title="This exact print isn't in the price source">
            {/* A DON card is never waiting on a price — it either came with one
                or the source has none for it. "PRICING…" would never resolve. */}
            {r.don || r.id in prices ? "NO PRICE" : "PRICING…"}
          </div>
        )}
        {/* The listing, and real sales, one link each. The catalogue price is
            one number per print; whether this alt art is really moving at that
            figure, only the listing and the sold history can say. */}
        <CardLinks
          game="onepiece"
          card={{ name: r.name, displayNumber: r.don ? r.set || "DON" : baseCode(r.id) }}
        />

        {/* The same door as in the Pokemon search, and so the same words. What
            is behind it differs by game — graded sales there, the other prints
            of this card number here, which run from $28 to $8,490 on one of
            them — but that is what the page is for, not what the link is. */}
        {onInspect && (
          <button
            type="button"
            className="btn btn-link card-inspect"
            onClick={() => onInspect({ ...toCard(r), marketPrice: price ?? null })}
            title={`View details for ${r.name}`}
          >
            View details →
          </button>
        )}

        <div className="card-actions">
          {/* Named for the card they act on. "Add" and "Want" are the whole
              accessible name otherwise, so a screen reader on a grid of sixty
              hears sixty identical buttons — the Pokemon rows next door have
              said which card since they were written. */}
          {onAddToCollection && (
            <button
              className="btn btn-add"
              onClick={() => onAddToCollection({ ...toCard(r), marketPrice: price ?? null })}
              disabled={isInCollection?.(r.id)}
              aria-label={
                isInCollection?.(r.id)
                  ? `${r.name} is already in your collection`
                  : `Add ${r.name}, ${label}, to your collection`
              }
            >
              {isInCollection?.(r.id) ? "In collection" : "Add to collection"}
            </button>
          )}
          <button
            className="btn btn-want"
            onClick={() => { onAdd(toCard(r)); setAdded((n) => n + 1); }}
            disabled={already}
            aria-label={
              already
                ? `${r.name} is already on the list`
                : `Add ${r.name}, ${label}, to the list`
            }
          >
            {already ? "Added" : onAddToCollection ? "Want" : "Add"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="op-search op-search--open">
      <div className="op-search-head">
        <h3 className="op-search-title">
          {alwaysOpen ? "Search" : <>Add cards{targetName ? <> to <em>{targetName}</em></> : null}</>}
        </h3>
        {!alwaysOpen && (
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => { setOpen(false); setQuery(""); setAdded(0); }}
        >
          Done
        </button>
        )}
      </div>

      <div className="op-search-row">
        <input
          autoFocus
          className="search-input"
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShown(PAGE); setDonShown(DON_PAGE); }}
          placeholder="Card name, or the code printed on it (OP01-121)"
          aria-label="Search One Piece cards"
        />
        {query && (
          <button
            className="btn btn-secondary"
            onClick={() => { setQuery(""); setShown(PAGE); setDonShown(DON_PAGE); }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Only once there is something to narrow. A row of filters over an
          empty search is a row of questions about nothing. */}
      {/* Also whenever DON is hidden, or a search found only DON cards — the
          control that hides them has to stay reachable to unhide them. */}
      {(found.length > 1 || donResults.length > 0 || (hideDon && query.trim())) && (
        <div className="op-filters" role="group" aria-label="Narrow the results">
          <select
            className="op-filter"
            value={setFilter}
            onChange={(e) => { setSetFilter(e.target.value); setShown(PAGE); }}
            aria-label="Set"
          >
            <option value="">All sets</option>
            {setsFound.map((s) => (
              <option key={s} value={s}>{sets?.[s]?.name ? `${s} · ${sets[s].name}` : s}</option>
            ))}
          </select>
          <select
            className="op-filter"
            value={rarityFilter}
            onChange={(e) => { setRarityFilter(e.target.value); setShown(PAGE); }}
            aria-label="Rarity"
          >
            <option value="">All rarities</option>
            {raritiesFound.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <label className="check-inline op-filter-alt">
            <input
              type="checkbox"
              checked={altOnly}
              onChange={(e) => { setAltOnly(e.target.checked); setShown(PAGE); }}
            />
            Alt arts only
          </label>
          <label className="check-inline op-filter-alt">
            <input
              type="checkbox"
              checked={hideDon}
              onChange={(e) => toggleDon(e.target.checked)}
            />
            Hide DON!!
          </label>
          {filtering && (
            <button
              type="button"
              className="btn btn-link op-filter-reset"
              onClick={() => { setSetFilter(""); setRarityFilter(""); setAltOnly(false); }}
            >
              Show all {found.length}
            </button>
          )}
        </div>
      )}

      <p className="op-search-note">
        {tooShort
          ? "Keep typing — two letters at least."
          : query.trim()
          ? /* Both groups counted. Reporting only the numbered cards would say
               "62 matches" to a search for "don" that also found 187 of them.
               And what the filters set aside is said, because a count that
               silently excludes things is how "no results" gets reported as
               a bug against a search that found sixty. */
            `${results.length + donResults.length} match${
              results.length + donResults.length === 1 ? "" : "es"
            }${donResults.length ? ` · ${donResults.length} DON!!` : ""}${
              filtering && found.length !== results.length
                ? ` · ${found.length - results.length} hidden by filters`
                : ""
            }${added > 0 ? ` · ${added} added` : ""}`
          /* "Code" meant nothing on its own. It's the set-and-number printed
             at the bottom of the card, and showing one says it faster than
             explaining it. */
          : `${OP_CARD_COUNT.toLocaleString()} cards. Try a name like Zoro, or a code like OP01-121. Nothing leaves your device.`}
      </p>

      {/* DON!! cards first, and named. They are the group a numbered-card
          search can never turn up, and "don" matches sixty-two Donquixote
          prints — so appended they landed on the second page of a search for
          the word that names them. */}
      {visibleDon.length > 0 && (
        <>
          <h4 className="op-group-title">
            DON!! cards · {donResults.length}
            {donResults.length > visibleDon.length && ` · showing ${visibleDon.length}`}
          </h4>
          <div className="card-grid op-search-results">{visibleDon.map(tile)}</div>
          {donResults.length > visibleDon.length && (
            <div style={{ textAlign: "center", marginTop: 12, marginBottom: 8 }}>
              <button className="btn btn-see-more" onClick={() => setDonShown((n) => n + DON_PAGE)}>
                More DON cards · {donResults.length - visibleDon.length} left
              </button>
            </div>
          )}
        </>
      )}

      {/* Only worth a heading when there is something above it to tell apart. */}
      {visible.length > 0 && visibleDon.length > 0 && (
        <h4 className="op-group-title">Cards · {results.length}</h4>
      )}

      {visible.length > 0 && (
        <div className="card-grid op-search-results">
          {visible.map(tile)}
        </div>
      )}

      {results.length > visible.length && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button className="btn btn-see-more" onClick={() => setShown((n) => n + PAGE)}>
            See More · {results.length - visible.length} left
          </button>
        </div>
      )}

      <CardZoom card={zoom} onClose={() => setZoom(null)} />
    </div>
  );
}
